import type { FilteredPartnerStats } from '../../lib/partnerStatsFilters';
import { countryCodeToFlag, countryCodeToName } from '../../lib/countryUtils';
import styles from './CampaignAnalyticsPanel.module.css';

interface CampaignAnalyticsPanelProps {
  stats: FilteredPartnerStats;
}

function pct(numerator: number, denominator: number): string | null {
  if (denominator <= 0) return null;
  return `${((numerator / denominator) * 100).toFixed(0)}%`;
}

export function CampaignAnalyticsPanel({ stats }: CampaignAnalyticsPanelProps) {
  const sortedCountries = Object.entries(stats.countryBreakdown).sort(([, a], [, b]) => b - a);

  return (
    <div className={styles.panel}>
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>By platform</h3>
        <div className={styles.platformTable}>
          <div className={styles.platformHeader}>
            <span />
            <span>iOS</span>
            <span>Android</span>
          </div>
          <div className={styles.platformRow}>
            <span className={styles.platformLabel}>Clicks</span>
            <span className={styles.platformValue}>{stats.clicksIos}</span>
            <span className={styles.platformValue}>{stats.clicksAndroid}</span>
          </div>
          <div className={styles.platformRow}>
            <span className={styles.platformLabel}>Installs</span>
            <span className={styles.platformValue}>{stats.installsIos}</span>
            <span className={styles.platformValue}>{stats.installsAndroid}</span>
          </div>
          {stats.clicksIos > 0 || stats.clicksAndroid > 0 ? (
            <div className={styles.platformSubRow}>
              <span />
              <span className={styles.platformRate}>
                {pct(stats.installsIos, stats.clicksIos) ?? '—'} of clicks
              </span>
              <span className={styles.platformRate}>
                {pct(stats.installsAndroid, stats.clicksAndroid) ?? '—'} of clicks
              </span>
            </div>
          ) : null}
          <div className={styles.platformRow}>
            <span className={styles.platformLabel}>Purchases</span>
            <span className={styles.platformValue}>{stats.purchasesIos}</span>
            <span className={styles.platformValue}>{stats.purchasesAndroid}</span>
          </div>
          {stats.installsIos > 0 || stats.installsAndroid > 0 ? (
            <div className={styles.platformSubRow}>
              <span />
              <span className={styles.platformRate}>
                {pct(stats.purchasesIos, stats.installsIos) ?? '—'} of installs
              </span>
              <span className={styles.platformRate}>
                {pct(stats.purchasesAndroid, stats.installsAndroid) ?? '—'} of installs
              </span>
            </div>
          ) : null}
        </div>
        {stats.clicksDesktop > 0 ? (
          <p className={styles.desktopNote}>{stats.clicksDesktop} desktop clicks (not shown above)</p>
        ) : null}
      </div>

      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Top countries (installs)</h3>
        {sortedCountries.length === 0 ? (
          <p className={styles.emptyCountries}>No install country data yet</p>
        ) : (
          <div className={styles.countryList}>
            {sortedCountries.slice(0, 10).map(([code, count]) => (
              <div key={code} className={styles.countryRow}>
                <span className={styles.countryName}>
                  {countryCodeToFlag(code)} {countryCodeToName(code)}
                </span>
                <span className={styles.countryCount}>
                  {count} {count === 1 ? 'install' : 'installs'}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
